import { Component, OnDestroy, OnInit, SecurityContext, inject } from '@angular/core';
import { SafeResourceUrl } from '@angular/platform-browser';
import { ActivatedRoute, Router } from '@angular/router';

import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { EMPTY, Observable } from 'rxjs';
import { catchError, filter, map, shareReplay, switchMap, takeUntil, tap } from 'rxjs/operators';

import { AsyncPipe, NgIf } from '@angular/common';
import { BaseContainer } from '../../../core/common/containers/base.abstract.container';
import { IconSize } from '../../../elements/components/icon/entities/icon-sizes.entity';
import { InlineDescriptionListItem } from '../../../elements/components/inline-description-list/entities/inline-description-list-item.entity';
import {
    NavigationItem,
    NavigationItemIconPosition
} from '../../../elements/components/navigation-item/entities/navigation-item.entity';
import { NotificationType } from '../../../elements/components/notification/entities/notification-types.entity';
import { ShNotificationComponent } from '../../../elements/components/notification/notification.component';
import { ShPageHeaderComponent } from '../../../elements/components/page-header/page-header.component';
import { SafeResourceUrlPipe } from '../../../pipes/safe-resource-url.pipe';
import { MetabaseReport } from '../../entities/report.entity';
import { ShReportsSandboxService } from '../../sandbox/reports-sandbox.service';

@Component({
    selector: 'sh-report-detail-container',
    styleUrls: ['./report-detail-container.component.scss'],
    templateUrl: './report-detail-container.component.html',
    standalone: true,
    imports: [ShPageHeaderComponent, NgIf, ShNotificationComponent, AsyncPipe, TranslateModule, SafeResourceUrlPipe]
})
export class ShReportDetailContainerComponent extends BaseContainer implements OnInit, OnDestroy {
    private statefullReport$!: Observable<MetabaseReport | null>;

    readonly notificationType: typeof NotificationType = NotificationType;
    readonly securityContext: typeof SecurityContext = SecurityContext;

    currentReportId$!: Observable<string>;
    pureReport$!: Observable<MetabaseReport>;

    reportTitle$!: Observable<string>;
    reportEmbedUrl$!: Observable<SafeResourceUrl>;
    reportDescription$!: Observable<InlineDescriptionListItem[]>;

    navigationItems!: NavigationItem[];

    // Injectors
    private readonly router: Router = inject(Router);

    constructor(
        private readonly sandbox: ShReportsSandboxService,
        private readonly activatedRoute: ActivatedRoute,
        private readonly translate: TranslateService
    ) {
        super();
        this.setupNavigationItems();
    }

    ngOnInit(): void {
        this.startLoading();
        this.setupStateStreams();
        this.setupSourceStream();
        this.setupPresentationStreams();
    }

    ngOnDestroy(): void {
        super.ngOnDestroy();
    }

    private setupNavigationItems(): void {
        this.navigationItems = [
            {
                label: this.translate.instant('reports.detail.back-link'),
                icon: 'arrow-left',
                iconPosition: NavigationItemIconPosition.Start,
                navigationLink: {
                    route: '/reports'
                }
            }
        ];
    }

    private setupStateStreams(): void {
        this.currentReportId$ = this.activatedRoute.paramMap.pipe(
            takeUntil(this.destroy$),
            map(paramMap => paramMap.get('id') as string),
            shareReplay(1)
        );
    }

    private setupSourceStream(): void {
        this.statefullReport$ = this.currentReportId$.pipe(
            // MED-4783 always (re-)request report to ensure token is still valid
            switchMap(reportId => this.sandbox.getReport(reportId).pipe(catchError(() => this.showError()))),
            tap(() => this.stopLoading()),
            shareReplay(1)
        );

        this.pureReport$ = this.statefullReport$.pipe(
            filter((report: MetabaseReport | null): report is MetabaseReport => !!report),
            shareReplay(1)
        );
    }

    private setupPresentationStreams(): void {
        this.reportTitle$ = this.pureReport$.pipe(map(report => report.Label));
        this.reportEmbedUrl$ = this.pureReport$.pipe(map(report => report.EmbedUrl));
        this.reportDescription$ = this.pureReport$.pipe(
            map(report => report.Description),
            map(description => this.mapDescriptionToDescriptionList(description))
        );
    }

    private mapDescriptionToDescriptionList(description?: string): InlineDescriptionListItem[] {
        if (!description) {
            return [];
        }

        return [
            {
                icon: {
                    name: 'info',
                    size: IconSize.SMALL,
                    iconOnly: true
                },
                term: this.translate.instant('reports.detail.description'),
                details: description
            }
        ];
    }

    private startLoading(): void {
        this.error$.next(false);
        this.loading$.startLoading();
    }

    private stopLoading(): void {
        this.loading$.stopLoading();
    }

    private showError(): Observable<never> {
        this.loading$.stopLoading();
        this.error$.next(true);
        return EMPTY;
    }
}
